export interface CustomResolver {
    provider: string
    url: string
}

export const CUSTOM_DOH_KEY = 'dlt.v1.customDoh'
/** The DoH page probes every custom resolver on load, so the list is capped.
    Adding past the cap drops the oldest. */
export const MAX_CUSTOM = 10

/**
 * localStorage rather than one cookie per resolver.
 *
 * The old client wrote a `customdoh_<name>` cookie for each, which meant every
 * custom resolver was transmitted to the server on every request, and read back
 * by string-splitting document.cookie with filters that broke whenever any
 * other cookie appeared.
 */
export function readCustomResolvers(): CustomResolver[] {
    try {
        const raw = localStorage.getItem(CUSTOM_DOH_KEY)
        if (!raw) return []
        const parsed: unknown = JSON.parse(raw)
        if (!Array.isArray(parsed)) return []
        return parsed.filter((r): r is CustomResolver =>
            typeof r === 'object' && r !== null &&
            typeof (r as CustomResolver).provider === 'string' &&
            typeof (r as CustomResolver).url === 'string' &&
            isValidEndpoint((r as CustomResolver).url))
    } catch {
        return []
    }
}

function write(list: CustomResolver[]): CustomResolver[] {
    try {
        localStorage.setItem(CUSTOM_DOH_KEY, JSON.stringify(list))
    } catch {
        // private mode; the list will not persist beyond this session
    }
    return list
}

export function addCustomResolver(resolver: CustomResolver): CustomResolver[] {
    const rest = readCustomResolvers().filter((r) => r.url !== resolver.url)
    return write([resolver, ...rest].slice(0, MAX_CUSTOM))
}

export function removeCustomResolver(url: string): CustomResolver[] {
    return write(readCustomResolvers().filter((r) => r.url !== url))
}

/**
 * A DoH endpoint must be https: the browser will refuse a mixed-content request
 * from this page anyway, so accepting http would store something that can only
 * ever fail.
 *
 * The host may be a dotted name, an IPv4 literal, or a bracketed IPv6 literal.
 * That last case needs its own arm: URL.hostname returns IPv6 WITH the brackets
 * and it contains no dot, so a dot test alone rejects every IPv6 endpoint --
 * including Cloudflare's and Google's, and 44 of the resolvers in our own
 * directory. new URL() has already validated the address inside the brackets.
 */
export function isValidEndpoint(value: string): boolean {
    try {
        const url = new URL(value)
        if (url.protocol !== 'https:') return false
        const host = url.hostname
        if (host.startsWith('[') && host.endsWith(']')) return true
        return host.includes('.')
    } catch {
        return false
    }
}

export const MIN_PROVIDER_LENGTH = 3

export function validateProvider(value: string): string | undefined {
    const trimmed = value.trim()
    if (!trimmed) return 'This field is required'
    if (trimmed.length < MIN_PROVIDER_LENGTH) return `Must be at least ${MIN_PROVIDER_LENGTH} characters`
    return undefined
}

export function validateEndpoint(value: string): string | undefined {
    if (!value.trim()) return 'This field is required'
    if (!isValidEndpoint(value)) return 'Enter a valid https:// endpoint URL'
    return undefined
}
